import { AlertTriangle, ArrowRight, CalendarClock, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type { RetimePreview } from '@/lib/process/retimePreview'

/**
 * Moving a step's expected start, asked before it is done.
 *
 * Re-timing rewrites every hold on the step at once. The operator used to learn
 * what that meant only afterwards — six rows silently changing, or a one-line
 * refusal at the bottom of a form telling them a unit they could not see was
 * already running. Neither is a decision they were given the chance to make.
 *
 * So this states the whole move: every held unit, where it sits now, where it
 * would land. And when something blocks it, that is the FIRST thing said and
 * the confirm is closed off — the server refuses the step all-or-nothing, so
 * offering a button that is certain to fail would be a worse version of the
 * error it replaced.
 */
export function RetimeConfirmDialog({
  open,
  stepName,
  currentStart,
  newStart,
  preview,
  busy,
  error,
  formatStamp,
  onConfirm,
  onCancel,
}: {
  open: boolean
  stepName?: string
  /** Formatted current expected start, or null when none was set. */
  currentStart: string | null
  /** Formatted proposed expected start. */
  newStart: string
  preview: RetimePreview
  busy: boolean
  /** A refusal from the server, shown in place rather than closing the dialog. */
  error: string | null
  formatStamp: (iso: string) => string
  onConfirm: () => void
  onCancel: () => void
}) {
  const blocked = preview.blockedBy.length > 0

  return (
    <Dialog open={open} onOpenChange={o => !o && !busy && onCancel()}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-base'>
            <CalendarClock className='h-4 w-4 shrink-0 text-blue-600' />
            Change the expected start?
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-3'>
          <p className='text-sm text-gray-700'>
            {stepName ? (
              <>
                <span className='font-medium'>{stepName}</span> would start at{' '}
              </>
            ) : (
              'This step would start at '
            )}
            <span className='font-mono font-semibold tabular-nums'>{newStart}</span>
            {currentStart && (
              <>
                {' '}
                instead of{' '}
                <span className='font-mono tabular-nums text-gray-500'>{currentStart}</span>
              </>
            )}
            .
          </p>

          {/* THE BLOCKER FIRST. It decides whether any of the rest is possible,
              so burying it under the table would let an operator read a plan
              they cannot carry out. */}
          {blocked && (
            <div
              role='alert'
              className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900'
            >
              <p className='flex items-start gap-1.5 font-medium'>
                <AlertTriangle className='mt-px h-3.5 w-3.5 shrink-0 text-amber-600' />
                <span>
                  {preview.blockedBy.join(', ')}{' '}
                  {preview.blockedBy.length === 1 ? 'is' : 'are'} already in use
                </span>
              </p>
              <p className='mt-1 pl-5'>
                The run has begun on {preview.blockedBy.length === 1 ? 'it' : 'them'}, so the plan
                it started against cannot move. Release{' '}
                {preview.blockedBy.length === 1 ? 'it' : 'them'} to re-time this step.
              </p>
            </div>
          )}

          {preview.rows.length === 0 ? (
            <p className='rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600'>
              This step holds no equipment, so only the expected start itself changes.
            </p>
          ) : (
            <div className='overflow-hidden rounded-md border border-gray-200'>
              <div className='grid grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-x-2 border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-gray-500'>
                <span>Unit</span>
                <span className='text-right'>Now</span>
                <span />
                <span className='text-right'>After</span>
              </div>
              <ul className='divide-y divide-gray-100'>
                {preview.rows.map(row => (
                  <li
                    key={row.requirementId}
                    className={cn(
                      'grid grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-x-2 px-3 py-1.5 text-xs',
                      row.inUse && 'bg-amber-50/60',
                    )}
                  >
                    <span className='truncate font-mono font-medium text-gray-800'>
                      {row.label}
                    </span>
                    <span className='whitespace-nowrap text-right font-mono tabular-nums text-gray-500'>
                      {formatStamp(row.currentIso)}
                    </span>
                    <ArrowRight className='h-3 w-3 shrink-0 text-gray-300' />
                    <span
                      className={cn(
                        'whitespace-nowrap text-right font-mono tabular-nums',
                        row.inUse
                          ? 'text-gray-400 line-through'
                          : row.unchanged
                            ? 'text-gray-400'
                            : 'font-semibold text-gray-900',
                      )}
                    >
                      {/* A requirement with no run time has no window to move
                          to; saying so beats printing a blank cell. */}
                      {row.nextIso ? formatStamp(row.nextIso) : 'no run time'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!blocked && preview.rows.length > 0 && (
            <p className='text-xs text-gray-500'>
              {preview.movingCount === 0
                ? 'Every hold is already at this time — nothing would move.'
                : `${preview.movingCount} hold${preview.movingCount === 1 ? '' : 's'} would move. Each keeps its own prep, so they stay staggered. If a unit turns out to be busy at its new time, nothing moves at all.`}
            </p>
          )}

          {error && (
            <p
              role='alert'
              className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800'
            >
              {error} — the holds were left where they were.
            </p>
          )}
        </div>

        <DialogFooter className='gap-2 sm:gap-2'>
          <Button type='button' variant='ghost' onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            type='button'
            onClick={onConfirm}
            // Closed off when a started unit guarantees refusal — see the note
            // on the component.
            disabled={busy || blocked}
          >
            {busy && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
            {busy ? 'Moving…' : 'Change expected start'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
